import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, Sparkles, Heart, Briefcase, ArrowLeft,
  CheckCircle2, Send, Calendar, Clock, Rocket, Star
} from "lucide-react";
import emailjs from "@emailjs/browser";
import { useAuth } from "../../context/AuthContext";
import DonateCTA from "../Donate/DonateCTA";

// Import images
import Photo1 from "../../images/Photo_1.jpg";
import Photo2 from "../../images/Photo_2.jpg";
import Photo3 from "../../images/Photo_3.jpg";
import Photo4 from "../../images/Photo_4.jpg";
import Photo5 from "../../images/Photo_5.jpg";
import Photo6 from "../../images/Photo_6.jpg";
import Photo7 from "../../images/Photo_7.jpg";
import Photo8 from "../../images/Photo_8.jpg";
import Photo9 from "../../images/Photo_9.jpg";
import Photo10 from "../../images/Photo_10.jpg";
import Photo11 from "../../images/Photo_11.jpg";
import Photo12 from "../../images/Photo_12.jpg";

// Initialize EmailJS
emailjs.init("H55EiMp2HthaWX--h");

const GOOGLE_SHEET_URL = "https://script.google.com/macros/s/AKfycbw54_hW_GvCYNkDJpfcRJmuQFRURsMD46CEM5jSprPLr7-TlcFiQE5bG5whohqFUbJQVA/exec";

const impactStats = [
  { label: "Volunteers", count: "50K+", icon: Users },
  { label: "Events Done", count: "200+", icon: Calendar },
  { label: "Total Hours", count: "15K+", icon: Clock },
];

const benefits = [
  {
    icon: Rocket,
    title: "Real-World Impact",
    desc: "Work on live projects that directly transform communities across India.",
  },
  {
    icon: Users,
    title: "Mentorship",
    desc: "Learn from passionate leaders who've spent 20+ years building India's kindness movement.",
  },
  {
    icon: Heart,
    title: "Purpose-Driven Work",
    desc: "Go beyond your resume — develop empathy, leadership, and social responsibility.",
  },
  {
    icon: Star,
    title: "Certificate & Recognition",
    desc: "Earn a certificate of completion and a letter of recommendation from KU.",
  },
];

const eventCards = [
  {
    title: "Kindness Jam",
    imgSrc: Photo7,
    alt: "Kindness Jam group stage performance"
  },
  {
    title: "Seva Sandwich",
    imgSrc: Photo6,
    alt: "Volunteers packing sandwiches together"
  },
  {
    title: "BookMark Making",
    imgSrc: Photo8,
    alt: "Handcrafted bookmark workshops"
  },
  {
    title: "Eco-Bag Making",
    imgSrc: Photo4,
    alt: "Eco-friendly upcycling sessions"
  }
];

const internalRoles = [
  "Social Media & Content",
  "Communication (News and Media)",
  "Relationship Building",
  "Fund Raising",
  "Organization Building",
  "Web Development",
];

const JoinUsPortal = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [view, setView] = useState("home");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    college: "",
    role: "",
    motivation: "",
  });

  const [status, setStatus] = useState("idle");

  // Direct zero-delay navigation check
  const handleVolunteerRedirect = () => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (isAuthenticated || token || storedUser) {
      let role = user?.role;
      if (!role && storedUser) {
        try {
          role = JSON.parse(storedUser)?.role;
        } catch (e) {
          role = "student";
        }
      }
      navigate(role === "admin" ? "/admin/dashboard" : "/volunteer/dashboard");
    } else {
      navigate("/volunteer/login");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");

    try {
      const templateParams = {
        to_email: "contactus@kindnessunlimited.in",
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        college: formData.college,
        role: formData.role,
        message: formData.motivation,
      };

      await emailjs.send(
        "service_mvgf8yq",
        "template_zhxbke9",
        templateParams
      );

      const sheetData = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        sheetData.append(key, value);
      });
      sheetData.append("timestamp", new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }));

      await fetch(GOOGLE_SHEET_URL, {
        method: "POST",
        mode: "no-cors",
        body: sheetData,
      });

      setStatus("success");
    } catch (error) {
      console.error("Submission Failed:", error);
      setStatus("error");
      alert("Something went wrong. Please check the console for details.");
    }
  };

  return (
    <div className="min-h-screen bg-ku-cream text-ku-purple overflow-x-hidden w-full antialiased selection:bg-ku-purple/20">
      <AnimatePresence mode="wait">

        {/* VIEW 1: HOME COMMUNITY HUB */}
        {view === "home" && (
          <motion.div
            key="home-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="w-full flex flex-col"
          >
            <section className="relative w-full py-20 md:py-28 bg-gradient-to-br from-[#5D3294] to-[#3B1E60] text-white flex items-center justify-center overflow-hidden">
              <div className="w-full max-w-7xl mx-auto px-6 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

                <div className="lg:col-span-6 space-y-6 text-left">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-xs font-bold uppercase tracking-widest text-white/90">
                    <Sparkles className="w-4 h-4 text-ku-gold" />
                    Join the Movement
                  </div>

                  <h1 className="text-4xl sm:text-5xl md:text-6xl font-black leading-[1.15] tracking-tight">
                    Make An <br />
                    <span className="text-ku-gold drop-shadow-sm">
                      Impact Today
                    </span>
                  </h1>

                  <p className="text-white/90 text-lg md:text-xl max-w-xl leading-relaxed font-semibold">
                    Join thousands of young changemakers spreading kindness and creating positive change in communities worldwide.
                  </p>

                  <p className="italic text-ku-gold/90 text-base md:text-lg font-bold">
                    "No act of kindness is ever wasted."
                  </p>

                  <div className="flex flex-wrap items-center gap-4 pt-4">
                    <button
                      type="button"
                      onClick={handleVolunteerRedirect}
                      className="!rounded-full text-base font-bold tracking-wide px-8 py-3.5 inline-flex items-center justify-center gap-2.5 bg-white text-ku-purple shadow-xl hover:bg-gray-50 transition-all transform hover:-translate-y-0.5 whitespace-nowrap border-none cursor-pointer"
                    >
                      <Heart className="w-5 h-5 fill-current text-red-500" />
                      Start Volunteering
                    </button>

                    <button
                      type="button"
                      onClick={() => setView("internship-form")}
                      className="!rounded-full text-base font-bold tracking-wide px-8 py-3.5 inline-flex items-center justify-center gap-2.5 bg-white/10 text-white border border-white/30 backdrop-blur-md hover:bg-white/20 transition-all transform hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
                    >
                      <Briefcase className="w-5 h-5 text-ku-gold" />
                      Internship
                    </button>
                  </div>

                  <div className="grid grid-cols-3 gap-6 pt-10 border-t border-white/10 max-w-md">
                    {impactStats.map((stat) => (
                      <div key={stat.label} className="space-y-1">
                        <div className="text-2xl md:text-3xl font-black text-white">{stat.count}</div>
                        <div className="text-xs font-bold text-white/60 uppercase tracking-wider">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-6 w-full flex justify-center lg:justify-end">
                  <div className="relative w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl border border-white/10 aspect-[4/5]">
                    <img
                      src={Photo1}
                      alt="Kindness Unlimited Community Hub"
                      className="w-full h-full object-cover filter saturate-[1.15]"
                    />
                    <div className="absolute bottom-4 right-4 bg-white text-black p-4 rounded-2xl shadow-xl max-w-[160px] text-center border border-gray-100 flex flex-col items-center">
                      <div className="w-7 h-7 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 mb-1">
                        <Heart className="w-4 h-4 fill-current" />
                      </div>
                      <span className="text-[10px] uppercase font-black tracking-wider text-gray-400">This Week</span>
                      <span className="text-xl font-black text-black leading-tight">+1.2K</span>
                      <span className="text-[10px] font-bold text-gray-500 leading-normal">New Volunteers</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="py-24 bg-white w-full border-b border-gray-100">
              <div className="w-full max-w-7xl mx-auto px-6">
                <div className="text-center space-y-3 mb-16">
                  <h2 className="text-3xl md:text-5xl font-black tracking-tight text-ku-purple">Join Our Events</h2>
                  <p className="text-ku-purple/70 text-lg md:text-xl max-w-2xl mx-auto font-bold opacity-80">
                    Connect with fellow volunteers and make unforgettable memories
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                  {eventCards.map((card) => (
                    <div
                      key={card.title}
                      className="bg-ku-cream/40 rounded-2xl overflow-hidden border border-gray-200/60 shadow-sm group hover:border-ku-purple/30 transition-all"
                    >
                      <div className="aspect-[16/9] w-full bg-gray-100 relative overflow-hidden">
                        <img
                          src={card.imgSrc}
                          alt={card.alt}
                          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.01] transition-all duration-300"
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="text-xl font-black text-ku-purple tracking-wide">{card.title}</h3>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-24 bg-ku-cream/20 w-full">
              <div className="w-full max-w-7xl mx-auto px-6">
                <div className="text-center space-y-3 mb-16">
                  <h2 className="text-3xl md:text-5xl font-black tracking-tight text-ku-purple">Moments That Matter</h2>
                  <p className="text-ku-purple/70 text-lg md:text-xl max-w-2xl mx-auto font-bold opacity-80">
                    Capturing the joy, impact, and connections we create together
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 max-w-6xl mx-auto auto-rows-[220px]">
                  <div className="md:col-span-6 md:row-span-2 rounded-2xl overflow-hidden border border-gray-200 bg-white relative group shadow-sm">
                    <img src={Photo4} alt="Ecosystem Canvas Frame" className="w-full h-full object-cover transition-opacity" />
                  </div>
                  <div className="md:col-span-4 md:row-span-1 rounded-2xl overflow-hidden border border-gray-200 bg-white relative group shadow-sm">
                    <img src={Photo2} alt="Ecosystem Canvas Frame" className="w-full h-full object-cover transition-opacity" />
                  </div>
                  <div className="md:col-span-2 md:row-span-1 rounded-2xl overflow-hidden border border-gray-200 bg-white relative group shadow-sm">
                    <img src={Photo3} alt="Ecosystem Canvas Frame" className="w-full h-full object-cover transition-opacity" />
                  </div>
                  <div className="md:col-span-3 md:row-span-1 rounded-2xl overflow-hidden border border-gray-200 bg-white relative group shadow-sm">
                    <img src={Photo9} alt="Ecosystem Canvas Frame" className="w-full h-full object-cover transition-opacity" />
                  </div>
                  <div className="md:col-span-3 md:row-span-1 rounded-2xl overflow-hidden border border-gray-200 bg-white relative group shadow-sm">
                    <img src={Photo5} alt="Ecosystem Canvas Frame" className="w-full h-full object-cover transition-opacity" />
                  </div>
                  <div className="md:col-span-4 md:row-span-1 rounded-2xl overflow-hidden border border-gray-200 bg-white relative group shadow-sm">
                    <img src={Photo10} alt="Ecosystem Canvas Frame" className="w-full h-full object-cover transition-opacity" />
                  </div>
                  <div className="md:col-span-4 md:row-span-1 rounded-2xl overflow-hidden border border-gray-200 bg-white relative group shadow-sm">
                    <img src={Photo11} alt="Ecosystem Canvas Frame" className="w-full h-full object-cover transition-opacity" />
                  </div>
                  <div className="md:col-span-4 md:row-span-1 rounded-2xl overflow-hidden border border-gray-200 bg-white relative group shadow-sm">
                    <img src={Photo12} alt="Ecosystem Canvas Frame" className="w-full h-full object-cover transition-opacity" />
                  </div>
                </div>
              </div>
            </section>

            <DonateCTA
              variant="warm"
              headline="Fuel the Kindness Movement"
              subtext="Behind every volunteer is a community of supporters. Your donation keeps our programs running and helps us reach more lives every day."
            />
          </motion.div>
        )}

        {/* VIEW 2: INTERNSHIP FORM */}
        {view === "internship-form" && (
          <motion.div
            key="internship-view"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="w-full flex flex-col"
          >
            <section className="w-full py-16 md:py-20 bg-gradient-to-br from-white to-ku-cream border-b border-gray-100">
              <div className="w-full max-w-7xl mx-auto px-6">
                <button
                  type="button"
                  onClick={() => { setView("home"); setStatus("idle"); }}
                  className="inline-flex items-center gap-2 text-xl font-black text-gray-500 hover:text-ku-purple mb-8 transition-colors bg-transparent border-none outline-none cursor-pointer"
                >
                  <ArrowLeft className="w-6 h-6" />
                  Back to Community Hub
                </button>
              </div>
            </section>

            <section className="w-full py-16 bg-white border-b border-gray-100">
              <div className="w-full max-w-7xl mx-auto px-6">
                <div className="mb-12">
                  <span className="text-xs uppercase font-black tracking-wider text-gray-400 block mb-2">Why Intern With Us</span>
                  <h2 className="text-3xl font-black text-ku-purple">More Than an <span className="text-ku-gold">Internship</span></h2>
                  <p className="text-gray-600 font-bold mt-2 text-base max-w-xl">
                    This isn't just a line on your resume. It's a life-changing experience where you'll grow personally and professionally while making a real difference.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {benefits.map((benefit) => (
                    <div key={benefit.title} className="bg-ku-cream/20 border border-gray-200/60 rounded-2xl p-6 flex flex-col items-center text-center">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center mb-4 shadow-sm">
                        <benefit.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-bold text-ku-purple text-lg mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed font-medium">{benefit.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="w-full py-20 bg-ku-cream/40 relative">
              <div className="w-full max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">

                  <div className="lg:col-span-4 space-y-6">
                    <div>
                      <span className="inline-block px-4 py-2 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-sm font-black uppercase tracking-wider mb-4">
                        Apply Now
                      </span>
                      <h2 className="text-3xl md:text-4xl font-black leading-tight tracking-tight text-ku-purple">
                        Ready to Make a <span className="text-ku-gold">Difference</span>?
                      </h2>
                      <p className="text-gray-600 text-lg mt-4 leading-relaxed font-bold opacity-90">
                        Fill out the form. Internships are available year-round for students and young professionals.
                      </p>
                    </div>

                    <div className="space-y-4 bg-white border border-gray-200 p-6 rounded-2xl shadow-sm">
                      {[
                        "Flexible timing — remote & on-ground options",
                        "Open to students from all backgrounds",
                        "No prior experience required — just passion",
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <p className="text-ku-purple font-bold text-sm sm:text-base leading-snug">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="lg:col-span-8 w-full">
                    {status === "success" ? (
                      <div className="bg-white p-12 text-center rounded-2xl shadow-md border border-gray-200 max-w-2xl mx-auto">
                        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6 border border-emerald-100">
                          <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                        </div>
                        <h3 className="font-black text-ku-purple text-2xl mb-3 tracking-tight">
                          Application Received!
                        </h3>
                        <p className="text-ku-purple text-lg leading-relaxed max-w-lg mx-auto font-bold opacity-90">
                          Thank you for your interest in interning with Kindness Unlimited. We'll be in touch within 48 hours.
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="bg-white p-8 sm:p-10 rounded-3xl shadow-md border border-gray-200 space-y-6 max-w-4xl mx-auto">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="flex flex-col gap-2">
                            <label htmlFor="name" className="text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">Full Name *</label>
                            <input
                              id="name"
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-ku-cream/10 text-sm font-body font-bold text-ku-purple outline-none transition-all focus:border-ku-purple focus:bg-white placeholder:text-gray-400 placeholder:font-normal"
                              placeholder="Your full name"
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label htmlFor="email" className="text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">Email *</label>
                            <input
                              id="email"
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-ku-cream/10 text-sm font-body font-bold text-ku-purple outline-none transition-all focus:border-ku-purple focus:bg-white placeholder:text-gray-400 placeholder:font-normal"
                              placeholder="you@example.com"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div className="flex flex-col gap-2">
                            <label htmlFor="phone" className="text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">Phone</label>
                            <input
                              id="phone"
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-ku-cream/10 text-sm font-body font-bold text-ku-purple outline-none transition-all focus:border-ku-purple focus:bg-white placeholder:text-gray-400 placeholder:font-normal"
                              placeholder="+91 XXXXX XXXXX"
                            />
                          </div>
                          <div className="flex flex-col gap-2">
                            <label htmlFor="college" className="text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">College / Organisation</label>
                            <input
                              id="college"
                              type="text"
                              name="college"
                              value={formData.college}
                              onChange={handleChange}
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-ku-cream/10 text-sm font-body font-bold text-ku-purple outline-none transition-all focus:border-ku-purple focus:bg-white placeholder:text-gray-400 placeholder:font-normal"
                              placeholder="Your college or org"
                            />
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <label htmlFor="role" className="text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">Preferred Role *</label>
                          <div className="relative w-full">
                            <select
                              id="role"
                              name="role"
                              value={formData.role}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-ku-cream/10 text-sm font-body font-bold text-ku-purple outline-none transition-all focus:border-ku-purple focus:bg-white cursor-pointer appearance-none"
                              style={{ backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%233B1E60\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', backgroundSize: '14px' }}
                            >
                              <option value="" disabled className="font-normal text-gray-400">Select a role...</option>
                              {internalRoles.map((role) => (
                                <option key={role} value={role} className="font-bold">{role}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="flex flex-col gap-2">
                          <label htmlFor="motivation" className="text-xs font-black text-ku-purple uppercase tracking-wider opacity-80">Why do you want to intern with KU? *</label>
                          <textarea
                            id="motivation"
                            name="motivation"
                            value={formData.motivation}
                            onChange={handleChange}
                            required
                            rows={4}
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-ku-cream/10 text-sm font-body font-bold text-ku-purple outline-none transition-all focus:border-ku-purple focus:bg-white resize-none leading-relaxed placeholder:text-gray-400 placeholder:font-normal"
                            placeholder="Tell us what inspires you about kindness..."
                          />
                        </div>

                        <button
                          type="submit"
                          disabled={status === "submitting"}
                          className={`w-full py-4 !rounded-full font-black text-white shadow-md text-sm uppercase tracking-wider transition-all duration-300 inline-flex items-center justify-center gap-2 border-none cursor-pointer pt-3.5 ${
                            status === "submitting" ? "bg-gray-400 cursor-not-allowed" : "bg-gradient-to-r from-emerald-500 to-teal-500 hover:shadow-lg hover:scale-[1.01] active:scale-[0.99]"
                          }`}
                        >
                          <Send className="w-4 h-4" />
                          {status === "submitting" ? "Sending Application..." : "Submit Application"}
                        </button>
                      </form>
                    )}
                  </div>

                </div>
              </div>
            </section>
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

export default JoinUsPortal;